/**
 * Card moderno de campanha para listagem
 * Design com borda superior colorida por status, badges com glow, progresso com gradiente
 */

import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  Play,
  Pause,
  XCircle,
  Copy,
  Trash2,
  MoreVertical,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  Users,
  CheckCheck,
  MessageSquare,
  Pencil,
  ShieldCheck,
} from 'lucide-react';
import VisibilityBadge from '@/components/shared/VisibilityBadge';
import type { EntityVisibility } from '@/hooks/useEntityVisibility';

// Interface da campanha
interface Campaign {
  id: string;
  name: string;
  description: string | null;
  channel_type: string;
  message_content: string;
  status: string;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  visibility?: EntityVisibility | null;
}

// Interface para estatísticas de recipients (sincronizada com CampaignDetails)
export interface CampaignRecipientStats {
  total: number;
  pending: number;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
  skipped: number;
}

interface CampaignCardProps {
  campaign: Campaign;
  recipientStats?: CampaignRecipientStats; // Estatísticas calculadas via campaign_recipients
  canEdit: boolean;
  canCreate: boolean;
  canDelete: boolean;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onEdit: () => void; // Callback para editar campanha
  /** Abre o diálogo de gerenciamento de visibilidade. */
  onManageVisibility?: () => void;
}

// Configuração de status: ícone colorido + label em preto (sem background/badge)
const statusConfig: Record<string, {
  label: string;
  icon: any;
  borderColor: string;
  iconColor: string;
}> = {
  draft: {
    label: 'Rascunho',
    icon: Clock,
    borderColor: 'border-t-muted-foreground',
    iconColor: 'text-muted-foreground',
  },
  scheduled: {
    label: 'Agendada',
    icon: Clock,
    borderColor: 'border-t-blue-500',
    iconColor: 'text-blue-600',
  },
  running: {
    label: 'Em execução',
    icon: Play,
    borderColor: 'border-t-primary',
    iconColor: 'text-primary',
  },
  paused: {
    label: 'Pausada',
    icon: Pause,
    borderColor: 'border-t-yellow-500',
    iconColor: 'text-yellow-600',
  },
  completed: {
    label: 'Finalizada',
    icon: CheckCircle2,
    borderColor: 'border-t-green-500',
    iconColor: 'text-green-600',
  },
  cancelled: {
    label: 'Cancelada',
    icon: XCircle,
    borderColor: 'border-t-destructive',
    iconColor: 'text-destructive',
  },
  failed: {
    label: 'Falha',
    icon: AlertCircle,
    borderColor: 'border-t-destructive',
    iconColor: 'text-destructive',
  },
};

// Labels de canal
const channelLabels: Record<string, string> = {
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  instagram: 'Instagram',
  telegram: 'Telegram',
  webchat: 'WebChat',
};

export function CampaignCard({
  campaign,
  recipientStats,
  canEdit,
  canCreate,
  canDelete,
  onStart,
  onPause,
  onResume,
  onCancel,
  onDuplicate,
  onDelete,
  onEdit,
  onManageVisibility,
}: CampaignCardProps) {
  const navigate = useNavigate();
  const status = statusConfig[campaign.status] || statusConfig.draft;
  const StatusIcon = status.icon;
  
  // Usa estatísticas passadas via props (campaign_recipients) ou fallback para cálculo local
  // recipientStats é calculado diretamente da tabela campaign_recipients (fonte confiável)
  const stats = recipientStats || {
    total: campaign.total_recipients,
    pending: Math.max(0, campaign.total_recipients - campaign.sent_count - campaign.failed_count),
    sent: Math.max(0, campaign.sent_count - campaign.delivered_count),
    delivered: Math.max(0, campaign.delivered_count - campaign.read_count),
    read: campaign.read_count,
    failed: campaign.failed_count,
    skipped: 0,
  };

  // Calcula progresso (processados / total)
  const processedCount = stats.sent + stats.delivered + stats.read + stats.failed + stats.skipped;
  const progress = stats.total > 0
    ? Math.round((processedCount / stats.total) * 100)
    : 0;

  // Formata data
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Card
      className={cn(
        'group relative overflow-hidden border-t-4 transition-shadow duration-200',
        'hover:shadow-md',
        status.borderColor
      )}
    >

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Nome da campanha com hover effect */}
            <h3 
              className="text-lg font-semibold truncate cursor-pointer hover:text-primary transition-colors"
              onClick={() => navigate(`/tenant/campaigns/${campaign.id}`)}
            >
              {campaign.name}
            </h3>
            {campaign.description && (
              <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                {campaign.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Selo de visibilidade */}
            <VisibilityBadge visibility={campaign.visibility ?? 'public'} />

            {/* Status: ícone colorido + label em preto (sem badge) */}
            <div className="flex items-center gap-1 text-xs font-medium text-foreground">
              <StatusIcon className={cn('h-3.5 w-3.5', status.iconColor)} />
              <span>{status.label}</span>
            </div>

            {/* Menu de ações */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/tenant/campaigns/${campaign.id}`)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Visualizar
                </DropdownMenuItem>

                {onManageVisibility && (
                  <DropdownMenuItem onClick={onManageVisibility}>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Visibilidade
                  </DropdownMenuItem>
                )}

                {/* Editar - apenas para campanhas em draft ou scheduled */}
                {['draft', 'scheduled'].includes(campaign.status) && canEdit && (
                  <DropdownMenuItem onClick={onEdit}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar
                  </DropdownMenuItem>
                )}

                {campaign.status === 'draft' && canEdit && (
                  <DropdownMenuItem onClick={onStart}>
                    <Play className="h-4 w-4 mr-2" />
                    Iniciar
                  </DropdownMenuItem>
                )}

                {campaign.status === 'running' && canEdit && (
                  <DropdownMenuItem onClick={onPause}>
                    <Pause className="h-4 w-4 mr-2" />
                    Pausar
                  </DropdownMenuItem>
                )}

                {campaign.status === 'paused' && canEdit && (
                  <DropdownMenuItem onClick={onResume}>
                    <Play className="h-4 w-4 mr-2" />
                    Retomar
                  </DropdownMenuItem>
                )}

                {['running', 'paused'].includes(campaign.status) && canEdit && (
                  <DropdownMenuItem onClick={onCancel} className="text-destructive">
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancelar
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />

                {canCreate && (
                  <DropdownMenuItem onClick={onDuplicate}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicar
                  </DropdownMenuItem>
                )}

                {campaign.status === 'draft' && canDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onDelete} className="text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Excluir
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Metadata com ícones */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
          <Badge variant="outline" className="font-normal text-xs">
            <MessageSquare className="h-3 w-3 mr-1" />
            {channelLabels[campaign.channel_type] || campaign.channel_type}
          </Badge>
          <span className="text-muted-foreground/50">•</span>
          <span className="text-xs">Criada em {formatDate(campaign.created_at)}</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Barra de progresso clean — fina e sóbria */}
        {campaign.total_recipients > 0 && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Progresso</span>
              <span className="font-semibold text-foreground">{progress}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-muted-foreground/30 transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Estatísticas com ícones coloridos - Sincronizadas com campaign_recipients */}
        <div className="grid grid-cols-5 gap-1">
          {/* Total */}
          <div className="flex flex-col items-center p-1.5 rounded-lg bg-muted/50 transition-colors hover:bg-muted">
            <Users className="h-3.5 w-3.5 text-muted-foreground mb-0.5" />
            <span className="text-base font-bold">{stats.total}</span>
            <span className="text-[8px] text-muted-foreground uppercase">Total</span>
          </div>

          {/* Pendentes - Ainda não processados */}
          <div className="flex flex-col items-center p-1.5 rounded-lg bg-muted/50 transition-colors hover:bg-muted">
            <Clock className="h-3.5 w-3.5 text-muted-foreground mb-0.5" />
            <span className="text-base font-bold">{stats.pending}</span>
            <span className="text-[8px] text-muted-foreground uppercase">Pendentes</span>
          </div>

          {/* Entregues */}
          <div className="flex flex-col items-center p-1.5 rounded-lg bg-green-500/5 transition-colors hover:bg-green-500/10">
            <CheckCheck className="h-3.5 w-3.5 text-green-500 mb-0.5" />
            <span className="text-base font-bold text-green-600">{stats.delivered}</span>
            <span className="text-[8px] text-muted-foreground uppercase">Entregues</span>
          </div>

          {/* Lidos */}
          <div className="flex flex-col items-center p-1.5 rounded-lg bg-primary/5 transition-colors hover:bg-primary/10">
            <Eye className="h-3.5 w-3.5 text-primary mb-0.5" />
            <span className="text-base font-bold text-primary">{stats.read}</span>
            <span className="text-[8px] text-muted-foreground uppercase">Lidos</span>
          </div>

          {/* Falhas */}
          <div className="flex flex-col items-center p-1.5 rounded-lg bg-destructive/5 transition-colors hover:bg-destructive/10">
            <XCircle className="h-3.5 w-3.5 text-destructive mb-0.5" />
            <span className="text-base font-bold text-destructive">{stats.failed}</span>
            <span className="text-[8px] text-muted-foreground uppercase">Falhas</span>
          </div>
        </div>

        {/* Data agendada ou de conclusão */}
        {campaign.scheduled_at && campaign.status === 'scheduled' && (
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-500/5 rounded-lg px-3 py-2">
            <Clock className="h-4 w-4" />
            <span>Agendada para: {formatDate(campaign.scheduled_at)}</span>
          </div>
        )}
        {campaign.completed_at && campaign.status === 'completed' && (
          <div className="flex items-center gap-1.5 text-xs text-foreground px-2 py-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            <span>Finalizada em: {formatDate(campaign.completed_at)}</span>
          </div>
        )}
        {campaign.status === 'cancelled' && campaign.completed_at && (
          <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/5 rounded-lg px-3 py-2">
            <XCircle className="h-4 w-4" />
            <span>Cancelada em: {formatDate(campaign.completed_at)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
